import React, { useState } from 'react';
import { Settings as SettingsIcon, Save, Globe, Palette, Clock, Mail, Send, Shield, AlertCircle, Power, Cloud, Database, RefreshCw, Play, CreditCard, ToggleLeft, Sparkles, Eye, EyeOff, Bot, DollarSign } from 'lucide-react';
import { THEME_PRESETS, DEFAULT_THEME_PRESET, NAV_STYLES, DEFAULT_NAV_STYLE, CONTENT_LAYOUTS, DEFAULT_CONTENT_LAYOUT } from '../../theme/presets';
import Card from '../ui/Card';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import axios from 'axios';

// Features definition with metadata - OUTSIDE component to avoid recreation on every render
const FEATURES_METADATA = {
  lead_finder: { name: 'lead_finder', display_name: 'Lead Finder', description: 'Find B2B leads with AI-powered search and filtering', icon: '🔍', order: 0 },
  ocean_io: { name: 'ocean_io', display_name: 'Ocean.io', description: 'Scrape contacts from Ocean.io platform', icon: '🌊', order: 1 },
  sales_navigator: { name: 'sales_navigator', display_name: 'Sales Navigator', description: 'LinkedIn Sales Navigator integration', icon: '💼', order: 2 },
  instagram: { name: 'instagram', display_name: 'Instagram', description: 'Instagram profile and contact scraping', icon: '📸', order: 3 },
  social_watch: { name: 'social_watch', display_name: 'Social Watch', description: 'Monitor social media activities', icon: '👁️', order: 4 },
  crunchbase: { name: 'crunchbase', display_name: 'Crunchbase', description: 'Company and investor data from Crunchbase', icon: '🏢', order: 5 },
  ai_enrichment: { name: 'ai_enrichment', display_name: 'AI Enrichment', description: 'AI-powered contact and company data enrichment', icon: '🤖', order: 6 }
};

const SettingsTab = ({ settings, settingsLoading, updateSettings, mode = 'superadmin' }) => {
  const [localSettings, setLocalSettings] = useState(settings || {});
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testEmailMessage, setTestEmailMessage] = useState('');
  const [testingS3, setTestingS3] = useState(false);
  const [testS3Message, setTestS3Message] = useState('');
  const [backupMsg, setBackupMsg] = useState('');
  const [recentBackups, setRecentBackups] = useState([]);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Upcoming Features State
  const [upcomingFeatures, setUpcomingFeatures] = useState({
    ocean_io: { enabled: false, show_coming_soon: true },
    sales_navigator: { enabled: false, show_coming_soon: true },
    instagram: { enabled: false, show_coming_soon: true },
    social_watch: { enabled: false, show_coming_soon: true },
    crunchbase: { enabled: false, show_coming_soon: true },
    ai_enrichment: { enabled: false, show_coming_soon: true },
    lead_finder: { enabled: false, show_coming_soon: true }
  });
  const [featuresMessage, setFeaturesMessage] = useState('');

  React.useEffect(() => {
    // CRITICAL: Don't overwrite state if user has unsaved changes!
    if (isDirty) {
      return;
    }

    if (settings) {
      setLocalSettings(settings);
      setSaving(false);
      // Load upcoming features from settings and merge with metadata
      if (settings.upcoming_features) {
        const mergedFeatures = {};
        // Merge each feature with its metadata to ensure all fields are present
        Object.keys(FEATURES_METADATA).forEach(featureKey => {
          const savedFeature = settings.upcoming_features[featureKey] || {};
          mergedFeatures[featureKey] = {
            ...FEATURES_METADATA[featureKey], // Default metadata
            enabled: savedFeature.enabled !== undefined ? savedFeature.enabled : false,
            show_coming_soon: savedFeature.show_coming_soon !== undefined ? savedFeature.show_coming_soon : true
          };
        });
        setUpcomingFeatures(mergedFeatures);
      } else {
        // No upcoming_features in settings, use defaults
        const defaultFeatures = {};
        Object.keys(FEATURES_METADATA).forEach(featureKey => {
          defaultFeatures[featureKey] = {
            ...FEATURES_METADATA[featureKey],
            enabled: false,
            show_coming_soon: true
          };
        });
        setUpcomingFeatures(defaultFeatures);
      }
    }
  }, [settings, isDirty]);

  const handleSettingChange = (field, value) => {
    setLocalSettings(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      // Include upcoming features in the settings update
      const updatedSettings = {
        ...localSettings,
        upcoming_features: upcomingFeatures
      };
      await updateSettings(updatedSettings);
      setIsDirty(false);
      setSaving(false);
      setSaveSuccess(true);
      // Hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings. Please try again.');
      setSaving(false);
    }
  };

  // Toggle individual feature flag (updates local state only, saved when "Save Settings" is clicked)
  const toggleFeatureFlag = (featureName, field, value) => {
    setUpcomingFeatures(prev => {
      const updated = {
        ...prev,
        [featureName]: {
          ...FEATURES_METADATA[featureName], // Include metadata
          ...prev[featureName], // Preserve existing settings
          [field]: value // Update the specific field
        }
      };
      return updated;
    });
    setIsDirty(true);
  };

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleTestEmail = async () => {
    if (!testEmailAddress) {
      setTestEmailMessage('Please enter an email address to test');
      return;
    }

    try {
      setTestingEmail(true);
      setTestEmailMessage('');
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.post(`${API_BASE_URL}/api/admin/test-email`, { test_email: testEmailAddress }, { headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' } });
      setTestEmailMessage(`✅ ${response.data.message}`);
    } catch (error) {
      console.error('Test email error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to send test email';
      setTestEmailMessage(`❌ ${errorMsg}`);
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestS3 = async () => {
    try {
      setTestingS3(true);
      setTestS3Message('');
      const adminToken = localStorage.getItem('admin_token');
      const resp = await axios.post(`${API_BASE_URL}/api/admin/settings/test-s3`, {}, { headers: { 'Authorization': `Bearer ${adminToken}` } });
      const ok = resp.data?.overall_ok;
      if (ok) setTestS3Message('✅ S3 connectivity verified for both buckets');
      else setTestS3Message(`❌ S3 test failed: excel: ${resp.data?.excel?.message}; backups: ${resp.data?.backups?.message}`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Unknown S3 error';
      setTestS3Message(`❌ S3 test failed: ${msg}`);
    } finally {
      setTestingS3(false);
    }
  };

  const runBackupNow = async () => {
    try {
      setBackupMsg('');
      const adminToken = localStorage.getItem('admin_token');
      const resp = await axios.post(`${API_BASE_URL}/api/admin/backups/run-now`, {}, { headers: { 'Authorization': `Bearer ${adminToken}` } });
      setBackupMsg(`✅ Backup created: ${resp.data.key}`);
      await loadRecentBackups();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Backup failed';
      setBackupMsg(`❌ ${msg}`);
    }
  };

  const testBackup = async () => {
    try {
      setBackupMsg('');
      const adminToken = localStorage.getItem('admin_token');
      const resp = await axios.post(`${API_BASE_URL}/api/admin/backups/test`, {}, { headers: { 'Authorization': `Bearer ${adminToken}` } });
      setBackupMsg(`✅ Test upload ok: ${resp.data.key}`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Test failed';
      setBackupMsg(`❌ ${msg}`);
    }
  };

  const loadRecentBackups = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const resp = await axios.get(`${API_BASE_URL}/api/admin/backups/recent`, { headers: { 'Authorization': `Bearer ${adminToken}` } });
      setRecentBackups(resp.data.items || []);
    } catch (e) { console.error('recent backups failed', e); }
  };

  React.useEffect(() => { loadRecentBackups(); }, []);

  const timezoneOptions = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
    { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
    { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
    { value: 'Europe/London', label: 'Greenwich Mean Time (London)' },
    { value: 'Europe/Paris', label: 'Central European Time (Paris)' },
    { value: 'Europe/Berlin', label: 'Central European Time (Berlin)' },
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (Tokyo)' },
    { value: 'Asia/Shanghai', label: 'China Standard Time (Shanghai)' },
    { value: 'Asia/Kolkata', label: 'India Standard Time (Kolkata)' },
    { value: 'Asia/Dubai', label: 'Gulf Standard Time (Dubai)' },
    { value: 'Australia/Sydney', label: 'Australian Eastern Time (Sydney)' },
    { value: 'Australia/Melbourne', label: 'Australian Eastern Time (Melbourne)' }
  ];

  // Helper to conditionally render sections by mode
  const isPartner = mode === 'partner-admin';

  const faviconInputRef = React.useRef(null);

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Card.Header>
          <Card.Title style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <SettingsIcon size={20} color="#667eea" />
            Application Settings
          </Card.Title>
        </Card.Header>
        <Card.Content>
          <div style={{ maxWidth: '1000px' }}>
            {/* Favicon */}
            <div style={{ marginBottom: '32px' }}>
              <Card style={{ padding: '20px', backgroundColor: '#f8fafc' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#22d3ee20' }}>
                    <Palette size={20} color="#06b6d4" />
                  </div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>Favicon</h3>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ width: 48, height: 48, borderRadius: 8, border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white' }}>
                    {localSettings?.favicon_data_url ? (
                      <img src={localSettings.favicon_data_url} alt="favicon" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                    ) : (
                      <div style={{ width: 32, height: 32, background: '#e5e7eb', borderRadius: 6 }} />
                    )}
                  </div>
                  <div>
                    <input ref={faviconInputRef} type="file" accept=".ico,.png,.svg,image/x-icon,image/vnd.microsoft.icon,image/png,image/svg+xml" style={{ display: 'none' }} onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const maxBytes = 256 * 1024;
                      if (file.size > maxBytes) { alert('Favicon file too large. Max 256 KB.'); return; }
                      const allowed = ['image/x-icon', 'image/vnd.microsoft.icon', 'image/png', 'image/svg+xml'];
                      if (!allowed.includes(file.type)) { alert('Unsupported format. Use .ico, .png, or .svg'); return; }
                      const reader = new FileReader();
                      reader.onload = () => {
                        const dataUrl = reader.result;
                        setLocalSettings(prev => ({ ...prev, favicon_data_url: dataUrl }));
                        setIsDirty(true);
                      };
                      reader.onerror = () => alert('Failed to read file');
                      reader.readAsDataURL(file);
                    }} />
                    <Button type="button" onClick={() => faviconInputRef.current && faviconInputRef.current.click()}>Upload Favicon</Button>
                    {localSettings?.favicon_data_url && (
                      <Button type="button" style={{ marginLeft: 12, backgroundColor: '#fee2e2', color: '#991b1b' }} onClick={() => { setLocalSettings(prev => ({ ...prev, favicon_data_url: null })); setIsDirty(true); }}>
                        Remove
                      </Button>
                    )}
                    <div style={{ color: '#6b7280', fontSize: 12, marginTop: 8 }}>
                      Max size 256 KB. Supported: .ico, .png, .svg
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* General Settings */}
            <div style={{ marginBottom: '32px' }}>
              <Card style={{ padding: '20px', backgroundColor: '#f8fafc' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#667eea20' }}>
                    <Palette size={20} color="#667eea" />
                  </div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>General Settings</h3>
                </div>
                <FormField label="Default Timezone" type="select" value={localSettings?.timezone || 'UTC'} onChange={(e) => handleSettingChange('timezone', e.target.value)} options={timezoneOptions} />
                <div style={{ marginTop: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={localSettings?.require_admin_approval || false} onChange={(e) => handleSettingChange('require_admin_approval', e.target.checked)} style={{ width: '18px', height: '18px', accentColor: '#667eea' }} />
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Require Admin Approval for New Signups</span>
                  </label>
                </div>
              </Card>
            </div>

            {/* Order Management Mode (Manual vs Payment Gateway) */}
            <div style={{ marginBottom: '32px' }}>
              <Card style={{ padding: '20px', backgroundColor: '#f8fafc' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#22c55e20' }}>
                    <ToggleLeft size={20} color="#22c55e" />
                  </div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>Order Management</h3>
                </div>
                <FormField label="Mode" type="select" value={localSettings?.payment_mode || 'manual'} onChange={(e) => handleSettingChange('payment_mode', e.target.value)} options={[{ value: 'manual', label: 'Manual' }, { value: 'ecommerce', label: 'Payment Gateway' }]} />
              </Card>
            </div>

            {/* Email Settings */}
            <div style={{ marginBottom: '32px' }}>
              <Card style={{ padding: '20px', backgroundColor: '#f8fafc' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#3b82f620' }}>
                    <Mail size={20} color="#3b82f6" />
                  </div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>Email Configuration</h3>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={localSettings?.email_enabled || false} onChange={(e) => handleSettingChange('email_enabled', e.target.checked)} style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }} />
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Enable Email Notifications</span>
                  </label>
                </div>
                {localSettings?.email_enabled && (
                  <>
                    {/* SMTP Server Settings */}
                    <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '16px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: '0 0 16px 0' }}>SMTP Server Settings</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '16px' }}>
                        <FormField label="SMTP Host" type="text" value={localSettings?.smtp_host || ''} onChange={(e) => handleSettingChange('smtp_host', e.target.value)} placeholder="smtp.gmail.com" />
                        <FormField label="SMTP Port" type="number" value={localSettings?.smtp_port || 587} onChange={(e) => handleSettingChange('smtp_port', parseInt(e.target.value))} placeholder="587" />
                      </div>
                      <FormField label="SMTP Username" type="text" value={localSettings?.smtp_username || ''} onChange={(e) => handleSettingChange('smtp_username', e.target.value)} placeholder="your-email@example.com" style={{ marginBottom: '16px' }} />
                      <FormField label="SMTP Password" type="password" value={localSettings?.smtp_password || ''} onChange={(e) => handleSettingChange('smtp_password', e.target.value)} placeholder="Your SMTP password or app password" style={{ marginBottom: '16px' }} />
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={localSettings?.smtp_use_tls !== false} onChange={(e) => handleSettingChange('smtp_use_tls', e.target.checked)} style={{ width: '16px', height: '16px', accentColor: '#3b82f6' }} />
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Use TLS/SSL Encryption</span>
                      </label>
                    </div>

                    {/* Sender Information */}
                    <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '16px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: '0 0 16px 0' }}>Sender Information</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <FormField label="From Email Address" type="email" value={localSettings?.smtp_from_email || ''} onChange={(e) => handleSettingChange('smtp_from_email', e.target.value)} placeholder="from@example.com" />
                        <FormField label="From Name" type="text" value={localSettings?.smtp_from_name || ''} onChange={(e) => handleSettingChange('smtp_from_name', e.target.value)} placeholder="Your App Name" />
                      </div>
                    </div>

                    {/* Test Email */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <FormField label="Test Email" type="email" value={testEmailAddress} onChange={(e) => setTestEmailAddress(e.target.value)} placeholder="your-email@example.com" />
                      <Button variant="secondary" onClick={handleTestEmail} loading={testingEmail} icon={<Send size={14} />}>Send Test Email</Button>
                      {testEmailMessage && <div style={{ fontSize: 12, color: testEmailMessage.startsWith('✅') ? '#166534' : '#991b1b' }}>{testEmailMessage}</div>}
                    </div>
                  </>
                )}
              </Card>
            </div>

            {/* Partner (User Subscriptions/Topups) Payment Gateway Settings */}
            <div style={{ marginBottom: '32px' }}>
              <Card style={{ padding: '20px', backgroundColor: '#f8fafc' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#10b98120' }}>
                    <CreditCard size={20} color="#10b981" />
                  </div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>Payment Gateways — User Subscriptions/Top-ups</h3>
                </div>

                {/* Razorpay Keys (existing) */}
                <div style={{ marginTop: 8 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>Razorpay (Test/Live)</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 12 }}>
                    <FormField label="Razorpay Mode" type="select" value={localSettings?.razorpay_mode || 'test'} onChange={(e) => handleSettingChange('razorpay_mode', e.target.value)} options={[{ value: 'test', label: 'Test' }, { value: 'live', label: 'Live' }]} />
                    <div />
                    <FormField label="Key ID (Test)" type="text" value={localSettings?.razorpay_test_key_id || ''} onChange={(e) => handleSettingChange('razorpay_test_key_id', e.target.value)} />
                    <FormField label="Key Secret (Test)" type="password" value={localSettings?.razorpay_test_key_secret || ''} onChange={(e) => handleSettingChange('razorpay_test_key_secret', e.target.value)} />
                    <FormField label="Key ID (Live)" type="text" value={localSettings?.razorpay_live_key_id || ''} onChange={(e) => handleSettingChange('razorpay_live_key_id', e.target.value)} />
                    <FormField label="Key Secret (Live)" type="password" value={localSettings?.razorpay_live_key_secret || ''} onChange={(e) => handleSettingChange('razorpay_live_key_secret', e.target.value)} />
                    <FormField label="Webhook Secret" type="password" value={localSettings?.razorpay_webhook_secret || ''} onChange={(e) => handleSettingChange('razorpay_webhook_secret', e.target.value)} />
                  </div>
                </div>

                {/* Razorpay Payment Links Configuration */}
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px dashed #e5e7eb' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <CreditCard size={16} color="#667eea" />
                    <h4 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>Payment Links Configuration</h4>
                  </div>
                  <div style={{ padding: '12px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe', marginBottom: 12 }}>
                    <p style={{ fontSize: '12px', color: '#1e40af', margin: 0, lineHeight: '1.5' }}>
                      <strong>Integration Mode:</strong> Choose between Razorpay Checkout (modal) or Payment Links (redirect).
                      Payment Links work from any domain without registration - useful for avoiding domain mismatch errors.
                    </p>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <FormField
                      label="Integration Mode"
                      type="select"
                      value={localSettings?.razorpay_integration_mode || 'checkout'}
                      onChange={(e) => handleSettingChange('razorpay_integration_mode', e.target.value)}
                      options={[
                        { value: 'checkout', label: 'Checkout (Modal)' },
                        { value: 'payment_links', label: 'Payment Links (Redirect)' }
                      ]}
                      helpText="Checkout: In-page modal. Payment Links: Redirect to Razorpay hosted page"
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                    <FormField
                      label="SMS Notifications"
                      type="checkbox"
                      checked={localSettings?.razorpay_payment_link_notify_sms || false}
                      onChange={(e) => handleSettingChange('razorpay_payment_link_notify_sms', e.target.checked)}
                      helpText="Send SMS to customer when payment link is created"
                    />
                    <FormField
                      label="Email Notifications"
                      type="checkbox"
                      checked={localSettings?.razorpay_payment_link_notify_email !== false}
                      onChange={(e) => handleSettingChange('razorpay_payment_link_notify_email', e.target.checked)}
                      helpText="Send email to customer when payment link is created"
                    />
                    <FormField
                      label="Payment Reminders"
                      type="checkbox"
                      checked={localSettings?.razorpay_payment_link_reminder_enabled || false}
                      onChange={(e) => handleSettingChange('razorpay_payment_link_reminder_enabled', e.target.checked)}
                      helpText="Send reminder notifications for pending payments"
                    />
                  </div>
                  <div style={{ marginTop: 12, padding: '10px', backgroundColor: '#fef3c7', borderRadius: '6px', border: '1px solid #fde68a' }}>
                    <p style={{ fontSize: '11px', color: '#92400e', margin: 0, lineHeight: '1.4' }}>
                      <strong>Note:</strong> Payment links expire after 24 hours (fixed). Links are created on-demand for each order.
                    </p>
                  </div>
                </div>

                {/* Stripe Keys (partner-managed) */}
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px dashed #e5e7eb' }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>Stripe (Test/Live)</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 12 }}>
                    <FormField label="Stripe Mode" type="select" value={localSettings?.partner_stripe_mode || 'test'} onChange={(e) => handleSettingChange('partner_stripe_mode', e.target.value)} options={[{ value: 'test', label: 'Test' }, { value: 'live', label: 'Live' }]} />
                    <div />
                    <FormField label="Secret Key (Test)" type="password" value={localSettings?.partner_stripe_secret_key_test || ''} onChange={(e) => handleSettingChange('partner_stripe_secret_key_test', e.target.value)} />
                    <FormField label="Secret Key (Live)" type="password" value={localSettings?.partner_stripe_secret_key_live || ''} onChange={(e) => handleSettingChange('partner_stripe_secret_key_live', e.target.value)} />
                    <FormField label="Webhook Secret (Test)" type="password" value={localSettings?.partner_stripe_webhook_secret_test || ''} onChange={(e) => handleSettingChange('partner_stripe_webhook_secret_test', e.target.value)} />
                    <FormField label="Webhook Secret (Live)" type="password" value={localSettings?.partner_stripe_webhook_secret_live || ''} onChange={(e) => handleSettingChange('partner_stripe_webhook_secret_live', e.target.value)} />
                  </div>
                </div>
              </Card>
            </div>

            {/* Meta Conversion API (CAPI) Settings */}
            <div style={{ marginBottom: '32px' }}>
              <Card style={{ padding: '20px', backgroundColor: '#f8fafc' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#1877f220' }}>
                    <SettingsIcon size={20} color="#1877f2" />
                  </div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>
                    Meta Conversion API (CAPI)
                  </h3>
                </div>
                
                <p style={{ 
                  fontSize: '14px', 
                  color: '#6b7280', 
                  marginBottom: '20px',
                  lineHeight: '1.5'
                }}>
                  Configure Meta (Facebook) Conversion API for tracking user events like registrations, purchases, and checkout initiations. This helps optimize your ad campaigns and track conversions accurately.
                </p>

                <div style={{ display: 'grid', gap: '16px' }}>
                  {/* Enable/Disable Toggle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <input
                      type="checkbox"
                      checked={localSettings?.meta_capi_enabled || false}
                      onChange={(e) => handleSettingChange('meta_capi_enabled', e.target.checked)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                      Enable Meta Conversion API Tracking
                    </label>
                  </div>

                  {/* Pixel ID */}
                  <FormField 
                    label="Meta Pixel ID" 
                    type="text" 
                    value={localSettings?.meta_pixel_id || ''} 
                    onChange={(e) => handleSettingChange('meta_pixel_id', e.target.value)}
                    placeholder="123456789012345"
                    disabled={!localSettings?.meta_capi_enabled}
                  />

                  {/* Access Token */}
                  <FormField 
                    label="Conversion API Access Token" 
                    type="password" 
                    value={localSettings?.meta_capi_access_token || ''} 
                    onChange={(e) => handleSettingChange('meta_capi_access_token', e.target.value)}
                    placeholder="Enter your Meta CAPI access token"
                    disabled={!localSettings?.meta_capi_enabled}
                  />

                  {localSettings?.meta_capi_enabled && (
                    <div style={{ 
                      padding: '12px', 
                      backgroundColor: '#dbeafe', 
                      borderRadius: '8px', 
                      fontSize: '14px', 
                      color: '#1e40af',
                      lineHeight: '1.5'
                    }}>
                      <strong>📈 Events Tracked:</strong>
                      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                        <li><strong>CompleteRegistration</strong> - When users verify their email</li>
                        <li><strong>InitiateCheckout</strong> - When users click "Pay Now" button</li>
                        <li><strong>Purchase</strong> - When payment is successful (all gateways)</li>
                      </ul>
                      Make sure your Meta Pixel ID and Access Token have the necessary permissions for Conversion API.
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* Superadmin-only sections */}
            {!isPartner && (
              <>
                {/* OpenAI API Configuration (Superadmin Only) */}
                <div style={{ marginBottom: '32px' }}>
                  <Card style={{ padding: '20px', backgroundColor: '#f8fafc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#10b98120' }}>
                        <Bot size={20} color="#10b981" />
                      </div>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>OpenAI API Configuration</h3>
                    </div>

                    <p style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      marginBottom: '20px',
                      lineHeight: '1.5'
                    }}>
                      Configure OpenAI API key for AI-powered features across the platform including AI enrichment, content generation, and data analysis.
                    </p>

                    <div style={{ display: 'grid', gap: '16px' }}>
                      <FormField
                        label="OpenAI API Key"
                        type="password"
                        value={localSettings?.openai_api_key || ''}
                        onChange={(e) => handleSettingChange('openai_api_key', e.target.value)}
                        placeholder="sk-..."
                        helpText="API key for GPT-4o and other OpenAI models"
                      />

                      <div style={{
                        padding: '12px',
                        backgroundColor: '#dbeafe',
                        borderRadius: '8px',
                        fontSize: '14px',
                        color: '#1e40af',
                        lineHeight: '1.5'
                      }}>
                        <strong>💡 Used for:</strong> AI enrichment, smart data extraction, content generation, and intelligent automation features
                      </div>

                      <div style={{
                        padding: '12px',
                        backgroundColor: '#fef3c7',
                        borderRadius: '8px',
                        fontSize: '13px',
                        color: '#92400e',
                        lineHeight: '1.5'
                      }}>
                        <strong>📝 Note:</strong> Bright Data API keys for web scraping are managed in the <strong>Scraper Settings</strong> tab
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Revenue Reporting */}
                <div style={{ marginBottom: '32px' }}>
                  <Card style={{ padding: '20px', backgroundColor: '#f8fafc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#10b98120' }}>
                        <DollarSign size={20} color="#10b981" />
                      </div>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>Revenue Reporting</h3>
                    </div>

                    <FormField
                      label="Revenue per 1000 Credits (USD)"
                      type="number"
                      step="0.01"
                      value={localSettings?.revenue_per_1000_usd || 2.5}
                      onChange={(e) => handleSettingChange('revenue_per_1000_usd', parseFloat(e.target.value))}
                      helpText="Used for revenue calculations in reporting dashboards"
                    />
                  </Card>
                </div>

                {/* Job Billing Mode (Superadmin Only - Phase 2) */}
                <div style={{ marginBottom: '32px' }}>
                  <Card style={{ padding: '20px', backgroundColor: '#f8fafc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#8b5cf620' }}>
                        <CreditCard size={20} color="#8b5cf6" />
                      </div>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>Job Billing Mode</h3>
                    </div>
                    <FormField 
                      label="Billing Mode" 
                      type="select" 
                      value={localSettings?.billing_mode || 'user_subscription'} 
                      onChange={(e) => handleSettingChange('billing_mode', e.target.value)} 
                      options={[
                        { value: 'user_subscription', label: 'User Subscription Only' }, 
                        { value: 'partner_wallet', label: 'Dual Billing (User + Partner Wallet)' }
                      ]} 
                    />
                    {localSettings?.billing_mode === 'partner_wallet' && (
                      <div style={{ 
                        marginTop: '12px', 
                        padding: '12px', 
                        backgroundColor: '#ddd6fe', 
                        borderRadius: '8px', 
                        border: '1px solid #c4b5fd' 
                      }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#6b21a8', marginBottom: '4px' }}>
                          ⚡ Dual Billing Active
                        </div>
                        <div style={{ fontSize: '13px', color: '#7c3aed', lineHeight: '1.4' }}>
                          When users run jobs, credits will be deducted from BOTH their subscription AND the Partner Wallet simultaneously.
                        </div>
                      </div>
                    )}
                  </Card>
                </div>

                {/* System Status Override */}
                <div style={{ marginBottom: '32px' }}>
                  <Card style={{ padding: '20px', backgroundColor: '#f8fafc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#f9731620' }}>
                        <Power size={20} color="#f97316" />
                      </div>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>System Status Override</h3>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <FormField label="Mode" type="select" value={localSettings?.system_status_mode || 'auto'} onChange={(e) => handleSettingChange('system_status_mode', e.target.value)} options={[{ value: 'auto', label: 'Auto' }, { value: 'up', label: 'Force Up' }, { value: 'down', label: 'Force Down' }]} />
                      <FormField label="Message (when Down)" type="text" value={localSettings?.system_status_message || ''} onChange={(e) => handleSettingChange('system_status_message', e.target.value)} placeholder="We are under maintenance..." />
                    </div>
                  </Card>
                </div>

                {/* Per-Scraper Status Management */}
                <div style={{ marginBottom: '32px' }}>
                  <Card style={{ padding: '20px', backgroundColor: '#f8fafc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#3b82f620' }}>
                        <Power size={20} color="#3b82f6" />
                      </div>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>Scraper Status Management</h3>
                    </div>

                    <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                      <p style={{ fontSize: '13px', color: '#92400e', margin: 0 }}>
                        <strong>Note:</strong> Setting "Force Down" in System Status Override above will disable ALL scrapers regardless of individual scraper settings.
                      </p>
                    </div>

                    {/* Apollo Scraper Status */}
                    <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0 }}>Apollo Scraper</h4>
                        {localSettings?.scraper_status?.apollo?.status === 'up' && (
                          <span style={{ padding: '2px 8px', backgroundColor: '#dcfce7', color: '#166534', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}>
                            Active
                          </span>
                        )}
                        {localSettings?.scraper_status?.apollo?.status === 'down' && (
                          <span style={{ padding: '2px 8px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}>
                            Down
                          </span>
                        )}
                        {localSettings?.scraper_status?.apollo?.status === 'maintenance' && (
                          <span style={{ padding: '2px 8px', backgroundColor: '#fef3c7', color: '#92400e', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}>
                            Maintenance
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 12 }}>
                        <FormField
                          label="Status"
                          type="select"
                          value={localSettings?.scraper_status?.apollo?.status || 'up'}
                          onChange={(e) => {
                            const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                            const adminUsername = currentUser.username || 'admin';
                            const newScraperStatus = {
                              ...localSettings?.scraper_status,
                              apollo: {
                                status: e.target.value,
                                message: localSettings?.scraper_status?.apollo?.message || null,
                                disabled_at: e.target.value !== 'up' ? new Date().toISOString() : null,
                                disabled_by: e.target.value !== 'up' ? adminUsername : null
                              }
                            };
                            handleSettingChange('scraper_status', newScraperStatus);
                          }}
                          options={[
                            { value: 'up', label: 'Up (Active)' },
                            { value: 'down', label: 'Down (Disabled)' },
                            { value: 'maintenance', label: 'Maintenance' }
                          ]}
                        />
                        <FormField
                          label="Message (shown to users when not 'Up')"
                          type="text"
                          value={localSettings?.scraper_status?.apollo?.message || ''}
                          onChange={(e) => {
                            const newScraperStatus = {
                              ...localSettings?.scraper_status,
                              apollo: {
                                ...localSettings?.scraper_status?.apollo,
                                message: e.target.value || null
                              }
                            };
                            handleSettingChange('scraper_status', newScraperStatus);
                          }}
                          placeholder="Apollo scraper is temporarily unavailable..."
                        />
                      </div>
                      {localSettings?.scraper_status?.apollo?.disabled_at && (
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                          Last changed: {new Date(localSettings.scraper_status.apollo.disabled_at).toLocaleString()}
                          {localSettings.scraper_status.apollo.disabled_by && ` by ${localSettings.scraper_status.apollo.disabled_by}`}
                        </div>
                      )}
                    </div>
                  </Card>
                </div>

                {/* Superadmin PG for Partner Wallet */}
                <div style={{ marginBottom: '32px' }}>
                  <Card style={{ padding: '20px', backgroundColor: '#f8fafc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#10b98120' }}>
                        <CreditCard size={20} color="#10b981" />
                      </div>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>Partner Wallet Payment Gateways (Superadmin)</h3>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <FormField label="Gateway for USD" type="select" value={localSettings?.superadmin_pg_usd || 'manual'} onChange={(e) => handleSettingChange('superadmin_pg_usd', e.target.value)} options={[{ value: 'manual', label: 'Manual' }, { value: 'stripe', label: 'Stripe' }, { value: 'razorpay', label: 'Razorpay' }]} />
                      <FormField label="Gateway for INR" type="select" value={localSettings?.superadmin_pg_inr || 'manual'} onChange={(e) => handleSettingChange('superadmin_pg_inr', e.target.value)} options={[{ value: 'manual', label: 'Manual' }, { value: 'stripe', label: 'Stripe' }, { value: 'razorpay', label: 'Razorpay' }]} />
                    </div>

                    {/* Stripe Keys */}
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px dashed #e5e7eb' }}>
                      <h4 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>Stripe (Test/Live)</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 12 }}>
                        <FormField label="Stripe Mode" type="select" value={localSettings?.superadmin_stripe_mode || 'test'} onChange={(e) => handleSettingChange('superadmin_stripe_mode', e.target.value)} options={[{ value: 'test', label: 'Test' }, { value: 'live', label: 'Live' }]} />
                        <div />
                        <FormField label="Secret Key (Test)" type="password" value={localSettings?.superadmin_stripe_secret_key_test || ''} onChange={(e) => handleSettingChange('superadmin_stripe_secret_key_test', e.target.value)} />
                        <FormField label="Secret Key (Live)" type="password" value={localSettings?.superadmin_stripe_secret_key_live || ''} onChange={(e) => handleSettingChange('superadmin_stripe_secret_key_live', e.target.value)} />
                        <FormField label="Webhook Secret (Test)" type="password" value={localSettings?.superadmin_stripe_webhook_secret_test || ''} onChange={(e) => handleSettingChange('superadmin_stripe_webhook_secret_test', e.target.value)} />
                        <FormField label="Webhook Secret (Live)" type="password" value={localSettings?.superadmin_stripe_webhook_secret_live || ''} onChange={(e) => handleSettingChange('superadmin_stripe_webhook_secret_live', e.target.value)} />
                      </div>
                    </div>

                    {/* Razorpay Keys */}
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px dashed #e5e7eb' }}>
                      <h4 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>Razorpay (Test/Live)</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 12 }}>
                        <FormField label="Razorpay Mode" type="select" value={localSettings?.superadmin_razorpay_mode || 'test'} onChange={(e) => handleSettingChange('superadmin_razorpay_mode', e.target.value)} options={[{ value: 'test', label: 'Test' }, { value: 'live', label: 'Live' }]} />
                        <div />
                        <FormField label="Key ID (Test)" type="text" value={localSettings?.superadmin_razorpay_test_key_id || ''} onChange={(e) => handleSettingChange('superadmin_razorpay_test_key_id', e.target.value)} />
                        <FormField label="Key Secret (Test)" type="password" value={localSettings?.superadmin_razorpay_test_key_secret || ''} onChange={(e) => handleSettingChange('superadmin_razorpay_test_key_secret', e.target.value)} />
                        <FormField label="Key ID (Live)" type="text" value={localSettings?.superadmin_razorpay_live_key_id || ''} onChange={(e) => handleSettingChange('superadmin_razorpay_live_key_id', e.target.value)} />
                        <FormField label="Key Secret (Live)" type="password" value={localSettings?.superadmin_razorpay_live_key_secret || ''} onChange={(e) => handleSettingChange('superadmin_razorpay_live_key_secret', e.target.value)} />
                        <FormField label="Webhook Secret" type="password" value={localSettings?.superadmin_razorpay_webhook_secret || ''} onChange={(e) => handleSettingChange('superadmin_razorpay_webhook_secret', e.target.value)} />
                      </div>
                    </div>
                  </Card>
                </div>

                {/* S3 (Contabo) Settings */}
                <div style={{ marginBottom: '32px' }}>
                  <Card style={{ padding: '20px', backgroundColor: '#f8fafc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#22c55e20' }}>
                        <Cloud size={20} color="#22c55e" />
                      </div>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>S3 Storage (Contabo)</h3>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <FormField label="Provider" type="text" value={localSettings?.s3_provider || 'contabo'} onChange={(e) => handleSettingChange('s3_provider', e.target.value)} />
                      <FormField label="Region" type="text" value={localSettings?.s3_region || 'eu2'} onChange={(e) => handleSettingChange('s3_region', e.target.value)} />
                      <FormField label="Endpoint URL" type="text" value={localSettings?.s3_endpoint_url || ''} onChange={(e) => handleSettingChange('s3_endpoint_url', e.target.value)} />
                      <FormField label="Public Base URL (optional)" type="text" value={localSettings?.s3_public_base_url || ''} onChange={(e) => handleSettingChange('s3_public_base_url', e.target.value)} />
                      <FormField label="Access Key ID" type="text" value={localSettings?.s3_access_key_id || ''} onChange={(e) => handleSettingChange('s3_access_key_id', e.target.value)} />
                      <FormField label="Secret Access Key" type="password" value={localSettings?.s3_secret_access_key || ''} onChange={(e) => handleSettingChange('s3_secret_access_key', e.target.value)} />
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="checkbox" checked={localSettings?.s3_force_path_style !== false} onChange={(e) => handleSettingChange('s3_force_path_style', e.target.checked)} />
                        Force Path Style
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="checkbox" checked={localSettings?.s3_verify_ssl !== false} onChange={(e) => handleSettingChange('s3_verify_ssl', e.target.checked)} />
                        Verify SSL
                      </label>
                      <FormField label="Signature Version" type="text" value={localSettings?.s3_signature_version || 's3v4'} onChange={(e) => handleSettingChange('s3_signature_version', e.target.value)} />
                      <FormField label="Server-side Encryption" type="select" value={localSettings?.s3_sse || 'AES256'} onChange={(e) => handleSettingChange('s3_sse', e.target.value)} options={[{ value: 'AES256', label: 'AES256' }, { value: 'none', label: 'None' }]} />
                    </div>

                    <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                      <Button variant="secondary" onClick={handleTestS3} loading={testingS3}>Test S3 Connectivity</Button>
                      {testS3Message && (
                        <div style={{ fontSize: 12, color: testS3Message.startsWith('✅') ? '#166534' : '#991b1b' }}>{testS3Message}</div>
                      )}
                    </div>
                  </Card>
                </div>

                {/* Excel Storage Policy */}
                <div style={{ marginBottom: '32px' }}>
                  <Card style={{ padding: '20px', backgroundColor: '#f8fafc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#60a5fa20' }}>
                        <Database size={20} color="#60a5fa" />
                      </div>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>Excel Storage Policy</h3>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr', gap: 16 }}>
                      <FormField label="Bucket" type="text" value={localSettings?.excel_storage_bucket || ''} onChange={(e) => handleSettingChange('excel_storage_bucket', e.target.value)} />
                      <FormField label="Prefix" type="text" value={localSettings?.excel_storage_prefix || 'jobs/'} onChange={(e) => handleSettingChange('excel_storage_prefix', e.target.value)} />
                      <FormField label="Format" type="select" value={localSettings?.excel_storage_format || 'xlsx'} onChange={(e) => handleSettingChange('excel_storage_format', e.target.value)} options={[{ value: 'xlsx', label: 'XLSX' }, { value: 'csv', label: 'CSV' }]} />
                      <FormField label="Presign TTL (seconds)" type="number" value={localSettings?.excel_storage_presign_ttl_seconds || 900} onChange={(e) => handleSettingChange('excel_storage_presign_ttl_seconds', parseInt(e.target.value))} />
                      <FormField label="Lifecycle (days)" type="number" value={localSettings?.excel_storage_lifecycle_days || 90} onChange={(e) => handleSettingChange('excel_storage_lifecycle_days', parseInt(e.target.value))} />
                      <FormField label="Upload Concurrency" type="number" value={localSettings?.excel_storage_upload_concurrency || 4} onChange={(e) => handleSettingChange('excel_storage_upload_concurrency', parseInt(e.target.value))} />
                    </div>
                  </Card>
                </div>

                {/* DB Backups Policy */}
                <div style={{ marginBottom: '32px' }}>
                  <Card style={{ padding: '20px', backgroundColor: '#f8fafc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#f59e0b20' }}>
                        <Database size={20} color="#f59e0b" />
                      </div>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>DB Backups Policy</h3>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr', gap: 16 }}>
                      <FormField label="Method" type="select" value={localSettings?.db_backups_method || 'mongodump'} onChange={(e) => handleSettingChange('db_backups_method', e.target.value)} options={[{ value: 'mongodump', label: 'mongodump (simple)' }, { value: 'pbm', label: 'Percona Backup (PITR)' }]} />
                      <FormField label="Bucket" type="text" value={localSettings?.db_backups_bucket || ''} onChange={(e) => handleSettingChange('db_backups_bucket', e.target.value)} />
                      <FormField label="Prefix" type="text" value={localSettings?.db_backups_prefix || 'mongo-backups/'} onChange={(e) => handleSettingChange('db_backups_prefix', e.target.value)} />
                      <FormField label="Cron (UTC)" type="text" value={localSettings?.db_backups_cron || '0 3 * * *'} onChange={(e) => handleSettingChange('db_backups_cron', e.target.value)} />
                      <FormField label="Retention (days)" type="number" value={localSettings?.db_backups_retention_days || 30} onChange={(e) => handleSettingChange('db_backups_retention_days', parseInt(e.target.value))} />
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="checkbox" checked={localSettings?.db_backups_encrypt_client_side || false} onChange={(e) => handleSettingChange('db_backups_encrypt_client_side', e.target.checked)} />
                        Client-side encrypt before upload
                      </label>
                    </div>

                    {/* Backup actions and recent list */}
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <Button variant="secondary" onClick={testBackup} icon={<RefreshCw size={14} />}>Test Backup</Button>
                      <Button variant="secondary" onClick={runBackupNow} icon={<Play size={14} />}>Run Backup Now</Button>
                      {backupMsg && (<div style={{ fontSize: 12, color: backupMsg.startsWith('✅') ? '#166534' : '#991b1b' }}>{backupMsg}</div>)}
                    </div>

                    <div style={{ marginTop: 16 }}>
                      <h4 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: '0 0 8px 0' }}>Recent Backups</h4>
                      {(!recentBackups || recentBackups.length===0) ? (
                        <div style={{ fontSize: 12, color: '#6b7280' }}>No backups listed yet</div>
                      ) : (
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', padding: '8px 12px', fontSize: 12, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                            <div>S3 Key</div>
                            <div>Size (bytes)</div>
                            <div>Last Modified</div>
                          </div>
                          {recentBackups.map((b) => (
                            <div key={b.key} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', padding: '8px 12px', fontSize: 12, color: '#111827', borderBottom: '1px solid #f3f4f6' }}>
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.key}</div>
                              <div>{b.size}</div>
                              <div>{b.last_modified ? new Date(b.last_modified).toLocaleString() : ''}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Card>
                </div>

                {/* Appearance / Theme (Superadmin Only).
                    Must stay inside this !isPartner fragment - SettingsTab is
                    also rendered by PartnerAdminTabRenderer with mode="partner-admin". */}
                <div style={{ marginBottom: '32px' }}>
                  <Card style={{ padding: '20px', backgroundColor: '#f8fafc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#667eea20' }}>
                        <Palette size={20} color="#667eea" />
                      </div>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>Appearance</h3>
                    </div>

                    <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px', lineHeight: '1.5' }}>
                      Choose the colour theme for the customer-facing app and sign-in pages.
                      Applies as soon as you save &mdash; no redeploy needed. The admin
                      dashboard is not affected.
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                      {THEME_PRESETS.map((preset) => {
                        const selected = (localSettings?.theme_preset || DEFAULT_THEME_PRESET) === preset.id;
                        return (
                          <button
                            key={preset.id}
                            type="button"
                            onClick={() => handleSettingChange('theme_preset', preset.id)}
                            style={{
                              textAlign: 'left',
                              padding: '14px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              background: 'white',
                              border: selected ? '2px solid #667eea' : '1px solid #e5e7eb',
                              boxShadow: selected ? '0 0 0 3px rgba(102, 126, 234, 0.12)' : 'none',
                              transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
                            }}
                          >
                            <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                              {preset.swatches.map((swatch) => (
                                <span
                                  key={swatch}
                                  style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '6px',
                                    backgroundColor: swatch,
                                    border: '1px solid rgba(0,0,0,0.06)'
                                  }}
                                />
                              ))}
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '2px' }}>
                              {preset.label}{selected ? ' — selected' : ''}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.4' }}>
                              {preset.description}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Sidebar</div>
                      <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px', lineHeight: '1.4' }}>
                        An independent choice &mdash; any sidebar style works with any colour theme.
                      </p>
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        {NAV_STYLES.map((nav) => {
                          const selected = (localSettings?.nav_style || DEFAULT_NAV_STYLE) === nav.id;
                          return (
                            <button
                              key={nav.id}
                              type="button"
                              onClick={() => handleSettingChange('nav_style', nav.id)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '10px',
                                textAlign: 'left', padding: '10px 14px', borderRadius: '8px',
                                cursor: 'pointer', background: 'white',
                                border: selected ? '2px solid #667eea' : '1px solid #e5e7eb',
                                boxShadow: selected ? '0 0 0 3px rgba(102, 126, 234, 0.12)' : 'none'
                              }}
                            >
                              <span style={{
                                width: 22, height: 22, borderRadius: '6px',
                                backgroundColor: nav.swatch, border: '1px solid rgba(0,0,0,0.12)'
                              }} />
                              <span>
                                <span style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                                  {nav.label}{selected ? ' \u2014 selected' : ''}
                                </span>
                                <span style={{ display: 'block', fontSize: '11px', color: '#6b7280' }}>{nav.description}</span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Page layout</div>
                      <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px', lineHeight: '1.4' }}>
                        How the customer-facing pages are arranged. Independent of colour and sidebar.
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '12px' }}>
                        {CONTENT_LAYOUTS.map((layout) => {
                          const selected = (localSettings?.content_layout || DEFAULT_CONTENT_LAYOUT) === layout.id;
                          return (
                            <button
                              key={layout.id}
                              type="button"
                              onClick={() => handleSettingChange('content_layout', layout.id)}
                              style={{
                                textAlign: 'left', padding: '12px 14px', borderRadius: '8px',
                                cursor: 'pointer', background: 'white',
                                border: selected ? '2px solid #667eea' : '1px solid #e5e7eb',
                                boxShadow: selected ? '0 0 0 3px rgba(102, 126, 234, 0.12)' : 'none'
                              }}
                            >
                              <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827', marginBottom: '3px' }}>
                                {layout.label}{selected ? ' \u2014 selected' : ''}
                              </div>
                              <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: '1.4' }}>{layout.description}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Logo/product name are build-time env vars, deliberately not
                        served over the API. Surfaced read-only so an operator does
                        not go looking for a control that cannot exist here. */}
                    <div style={{
                      marginTop: '20px',
                      paddingTop: '16px',
                      borderTop: '1px solid #e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      {settings?.product_logo ? (
                        <img src={settings.product_logo} alt="Current logo" style={{ height: 28, objectFit: 'contain' }} />
                      ) : (
                        <div style={{ width: 28, height: 28, borderRadius: '6px', backgroundColor: '#e5e7eb' }} />
                      )}
                      <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.4' }}>
                        Logo and product name are set at deploy time and cannot be changed here.
                        Updating them requires a new deployment with different environment variables.
                      </div>
                    </div>
                  </Card>
                </div>
              </>
            )}

                {/* Upcoming Features Management */}
                <div style={{ marginBottom: '32px' }}>
                  <Card style={{ padding: '20px', backgroundColor: '#f8fafc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: '#f59e0b20' }}>
                        <Sparkles size={20} color="#f59e0b" />
                      </div>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>
                        Upcoming Features Display
                      </h3>
                    </div>
                    
                    <p style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      marginBottom: '20px',
                      lineHeight: '1.5'
                    }}>
                      Control feature visibility and activation. "Enabled" makes features accessible, "Show Coming Soon" displays them with a badge when disabled.
                    </p>

                    {featuresMessage && (
                      <div style={{
                        padding: 12,
                        marginBottom: 16,
                        backgroundColor: featuresMessage.includes('✅') ? '#d1fae5' : '#fee2e2',
                        border: `1px solid ${featuresMessage.includes('✅') ? '#10b981' : '#ef4444'}`,
                        borderRadius: 8,
                        color: featuresMessage.includes('✅') ? '#065f46' : '#991b1b',
                        fontSize: '14px'
                      }}>
                        {featuresMessage}
                      </div>
                    )}

                    {/* Features List with Dual Toggles */}
                    <div style={{ marginBottom: 16 }}>
                      {Object.entries(FEATURES_METADATA)
                        .sort(([, a], [, b]) => (a.order || 0) - (b.order || 0))
                        .map(([key, metadata]) => {
                        const feature = { key, name: metadata.display_name, description: metadata.description, icon: metadata.icon };
                        const featureData = upcomingFeatures[feature.key] || {};
                        const isEnabled = featureData.enabled || false;
                        const showComingSoon = featureData.show_coming_soon === undefined ? true : featureData.show_coming_soon;

                        return (
                          <div
                            key={feature.key}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: 16,
                              backgroundColor: isEnabled ? '#f0fdf4' : 'white',
                              borderRadius: 8,
                              border: isEnabled ? '2px solid #10b981' : '1px solid #e5e7eb',
                              marginBottom: 12
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <span style={{ fontSize: 18 }}>{feature.icon}</span>
                                <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#111827' }}>
                                  {feature.name}
                                </h4>
                                {isEnabled && (
                                  <span style={{
                                    padding: '2px 8px',
                                    backgroundColor: '#10b981',
                                    color: 'white',
                                    borderRadius: 12,
                                    fontSize: 11,
                                    fontWeight: 600
                                  }}>
                                    ACTIVE
                                  </span>
                                )}
                              </div>
                              <p style={{ margin: 0, fontSize: 13, color: '#6b7280', lineHeight: 1.4 }}>
                                {feature.description}
                              </p>
                            </div>

                            <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                              {/* Enabled Toggle */}
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, fontWeight: 500 }}>
                                  Enabled
                                </div>
                                <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, cursor: 'pointer' }}>
                                  <input
                                    type="checkbox"
                                    checked={isEnabled}
                                    onChange={(e) => toggleFeatureFlag(feature.key, 'enabled', e.target.checked)}
                                    style={{ opacity: 0, width: 0, height: 0 }}
                                  />
                                  <span style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: isEnabled ? '#10b981' : '#d1d5db',
                                    transition: '0.3s',
                                    borderRadius: 24
                                  }}>
                                    <span style={{
                                      position: 'absolute',
                                      height: 18,
                                      width: 18,
                                      left: isEnabled ? 23 : 3,
                                      bottom: 3,
                                      backgroundColor: 'white',
                                      transition: '0.3s',
                                      borderRadius: '50%'
                                    }} />
                                  </span>
                                </label>
                              </div>

                              {/* Show Coming Soon Toggle */}
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, fontWeight: 500 }}>
                                  Show "Coming Soon"
                                </div>
                                <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, cursor: 'pointer' }}>
                                  <input
                                    type="checkbox"
                                    checked={showComingSoon}
                                    onChange={(e) => toggleFeatureFlag(feature.key, 'show_coming_soon', e.target.checked)}
                                    style={{ opacity: 0, width: 0, height: 0 }}
                                  />
                                  <span style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: showComingSoon ? '#10b981' : '#d1d5db',
                                    transition: '0.3s',
                                    borderRadius: 24
                                  }}>
                                    <span style={{
                                      position: 'absolute',
                                      height: 18,
                                      width: 18,
                                      left: showComingSoon ? 23 : 3,
                                      bottom: 3,
                                      backgroundColor: 'white',
                                      transition: '0.3s',
                                      borderRadius: '50%'
                                    }} />
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Info Note */}
                    <div style={{
                      padding: 16,
                      backgroundColor: '#eff6ff',
                      borderRadius: 8,
                      border: '1px solid #bfdbfe',
                      fontSize: 13,
                      color: '#1e40af',
                      lineHeight: 1.5
                    }}>
                      <strong>ℹ️ How it works:</strong> Toggle "Enabled" to activate features. Use "Show Coming Soon" to display disabled features with a badge. Click "Save Settings" below to apply changes.
                    </div>
                  </Card>
                </div>

            <div style={{ marginTop: 16 }}>
              <Button onClick={handleSaveSettings} loading={saving} icon={<Save size={14} />}>Save Settings</Button>
            </div>

          </div>
        </Card.Content>
      </Card>

      <div style={{ height: 24 }} />
      
      {/* Success Toast */}
      {saveSuccess && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          padding: '12px 16px',
          backgroundColor: '#10b981',
          color: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <Save size={16} />
          Settings saved successfully!
        </div>
      )}
    </div>
  );
};

export default SettingsTab;